import { useCallback } from "react";
import { config } from "../config";
import { useEventsApi } from "./useEventsApi";
import type {
  ReassignmentApplyResponse,
  ReassignmentMode,
  ReassignmentPreviewResponse,
} from "../shared/adminEventReassignment";
import type { NumberRangeBaseFunction, NumberRangeSubFunction } from "../shared/NumberRange";

type PreviewPayload =
  | {
      mode: "single";
      global_event_id: number;
      target_base_id: number;
      target_sub_id: number;
    }
  | {
      mode: "bulk";
      source_base_id: number;
      source_sub_id: number;
      target_base_id: number;
      target_sub_id: number;
    };

export const useAdminEventReassignmentApi = () => {
  const { callApi } = useEventsApi();

  const fetchBaseFunctions = useCallback(async (): Promise<NumberRangeBaseFunction[]> => {
    const res = await callApi(`${config.apiUrl}/api/number-range-base-function/`, "GET");
    return Array.isArray(res?.results) ? (res.results as NumberRangeBaseFunction[]) : [];
  }, [callApi]);

  const fetchSubFunctions = useCallback(async (baseId: number): Promise<NumberRangeSubFunction[]> => {
    const res = await callApi(`${config.apiUrl}/api/number-range-sub-function/?base=${baseId}`, "GET");
    return Array.isArray(res?.results) ? (res.results as NumberRangeSubFunction[]) : [];
  }, [callApi]);

  const previewReassignment = useCallback(
    async (payload: PreviewPayload): Promise<ReassignmentPreviewResponse> => {
      return callApi(`${config.apiUrl}/api/admin-event-reassignment/preview/`, "POST", payload);
    },
    [callApi]
  );

  const applyReassignment = useCallback(
    async (payload: PreviewPayload & { preview_token: string; confirm_text: string }): Promise<ReassignmentApplyResponse> => {
      return callApi(`${config.apiUrl}/api/admin-event-reassignment/apply/`, "POST", payload);
    },
    [callApi]
  );

  return {
    fetchBaseFunctions,
    fetchSubFunctions,
    previewReassignment,
    applyReassignment,
  };
};
